import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { Plus, ChevronRight, LifeBuoy, ArrowUpRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { cn } from "@/lib/utils";
import { listMyTickets } from "../api/supportApi";
import { SupportPresence } from "../components/SupportPresence";
import { StatusPill, relativeTime, statusLabel } from "../components/meta";

type T = Record<string, string>;

export function WidgetTicketList({
  onOpenTicket,
  onNew,
  onClose,
  t,
  language,
}: {
  onOpenTicket: (id: string) => void;
  onNew: () => void;
  onClose: () => void;
  t: T;
  language: string;
}) {
  const { data: tickets, isLoading } = useQuery({
    queryKey: ["support", "tickets"],
    queryFn: listMyTickets,
    refetchInterval: 5000,
  });

  return (
    <div className="flex h-full flex-col">
      <div className="flex-1 space-y-3 overflow-y-auto p-3">
        <SupportPresence className="rounded" />

        {isLoading ? (
          <div className="space-y-2">
            {[0, 1, 2].map((i) => (
              <div key={i} className="rounded border border-gray-200 p-3 dark:border-gray-800">
                <Skeleton className="h-3.5 w-2/3" />
                <Skeleton className="mt-2 h-3 w-1/3" />
              </div>
            ))}
          </div>
        ) : (tickets ?? []).length === 0 ? (
          <div className="flex flex-col items-center justify-center py-12 text-center">
            <div className="mb-3 flex h-12 w-12 items-center justify-center rounded-2xl bg-gray-100 dark:bg-gray-800">
              <LifeBuoy className="h-5 w-5 text-gray-400" />
            </div>
            <p className="max-w-[220px] text-sm text-gray-500 dark:text-gray-400">
              {t.emptyNoTicketsDesc}
            </p>
          </div>
        ) : (
          <div className="space-y-2">
            {(tickets ?? []).map((tk) => (
              <button
                key={tk.name}
                onClick={() => onOpenTicket(tk.name)}
                className="group flex w-full items-start gap-2 rounded border border-gray-200 bg-white p-3 text-left transition-colors hover:border-gray-300 dark:border-gray-800 dark:bg-gray-900/40 dark:hover:border-gray-700"
              >
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-1.5">
                    {tk.unread && (
                      <span className="h-2 w-2 shrink-0 rounded-full bg-primary" />
                    )}
                    <span className="truncate text-sm font-medium text-gray-900 dark:text-gray-100">
                      {tk.subject}
                    </span>
                  </div>
                  <p className="mt-0.5 truncate text-xs text-gray-500 dark:text-gray-400">
                    {tk.lastMessagePreview}
                  </p>
                  <div className="mt-1.5 flex items-center gap-2">
                    <StatusPill status={tk.status} label={statusLabel(t, tk.status)} />
                    <span className="text-[11px] text-gray-400 dark:text-gray-500">
                      {relativeTime(tk.lastActivityOn, language)}
                    </span>
                  </div>
                </div>
                <ChevronRight className="mt-0.5 h-4 w-4 shrink-0 text-gray-300 transition-transform group-hover:translate-x-0.5 dark:text-gray-600" />
              </button>
            ))}
          </div>
        )}
      </div>

      <div className="space-y-2 border-t border-gray-100 p-3 dark:border-gray-800">
        <Button className="w-full rounded-full" onClick={onNew}>
          <Plus className="h-4 w-4" />
          {t.newRequest}
        </Button>
        <Link
          to="/support"
          onClick={onClose}
          className="flex items-center justify-center gap-1 text-xs font-medium text-gray-500 transition-colors hover:text-gray-800 dark:text-gray-400 dark:hover:text-gray-200"
        >
          {t.widgetViewAll}
          <ArrowUpRight className={cn("h-3.5 w-3.5")} />
        </Link>
      </div>
    </div>
  );
}
